// Raspored.js

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../config/AuthProvider';
import Login from '../authPage/Login';
import RasporedTable from '../components/RasporedTable';
import { db } from '../config/firebase';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import '../css/Raspored.css'; // Uključujemo ažurirani CSS

function Raspored() {
  const [people, setPeople] = useState([]);
  const [weekSchedules, setWeekSchedules] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber(new Date()));
  const [selectedNationality, setSelectedNationality] = useState('Sve');
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState('');
  const { currentUser } = useContext(AuthContext);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  // Funkcija za dobivanje trenutnog tjedna
  function getWeekNumber(d) {
    // Kopiramo datum kako ne bismo mijenjali original
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    // Postavljamo na najbliži četvrtak: trenutni datum + 4 - trenutni dan u tjednu
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    // Dobivamo prvi dan u godini
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    // Izračunavamo broj tjedana
    const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNum;
  }

  // Funkcija za dobivanje datuma početka tjedna
  function getDateOfISOWeek(w, y) {
    const simple = new Date(Date.UTC(y, 0, 1 + (w - 1) * 7));
    const dow = simple.getDay();
    let ISOweekStart = simple;
    if (dow <= 4)
      ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
    else
      ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
    return ISOweekStart;
  }

  // Funkcija za dohvaćanje korisnika
  const fetchUsers = async () => {
    try {
      const qUsers = query(collection(db, 'users'));
      const querySnapshot = await getDocs(qUsers);
      const users = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPeople(users);
    } catch (e) {
      console.error('Greška pri dohvaćanju korisnika:', e);
    }
  };

  // Funkcija za dohvaćanje firmi
  const fetchFirms = async () => {
    try {
      const firmsCollection = collection(db, 'firms');
      const q = query(firmsCollection);
      const querySnapshot = await getDocs(q);
      const firmsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFirms(firmsData);
    } catch (error) {
      console.error('Greška pri dohvaćanju firmi:', error);
    }
  };

  // Funkcija za dohvaćanje rasporeda tjedna
  const fetchWeekSchedules = async () => {
    try {
      const weekStartDate = getDateOfISOWeek(selectedWeek, selectedYear);
      const weekStartStr = weekStartDate.toISOString().split('T')[0]; // Format 'YYYY-MM-DD'
      console.log('Početak tjedna:', weekStartStr);
      const qSchedules = query(
        collection(db, 'weekSchedule'),
        where('weekStart', '==', weekStartStr)
      );
      const querySnapshot = await getDocs(qSchedules);
      const schedules = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setWeekSchedules(schedules);
    } catch (e) {
      console.error('Greška pri dohvaćanju rasporeda:', e);
    }
  };

  // Funkcija za ažuriranje weekSchedules stanja nakon promjene
  const updateWeekSchedule = (updatedSchedule) => {
    setWeekSchedules((prevSchedules) =>
      prevSchedules.map((schedule) =>
        schedule.id === updatedSchedule.id ? updatedSchedule : schedule
      )
    );
  };

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
      fetchFirms();
      fetchWeekSchedules();
    }
  }, [currentUser, selectedYear, selectedWeek]);

  // Funkcija za prethodni tjedan
  const handlePreviousWeek = () => {
    setSelectedWeek((prevWeek) => {
      if (prevWeek > 1) {
        return prevWeek - 1;
      } else {
        setSelectedYear((prevYear) => prevYear - 1);
        return 52; // Ili 53 ako godina ima 53 tjedna
      }
    });
  };

  // Funkcija za sljedeći tjedan
  const handleNextWeek = () => {
    setSelectedWeek((prevWeek) => {
      if (prevWeek < 52) {
        return prevWeek + 1;
      } else {
        setSelectedYear((prevYear) => prevYear + 1);
        return 1;
      }
    });
  };

  // Funkcija za pozivanje Cloud Function i potvrđivanje rasporeda
  const triggerUpdate = async () => {
    setIsUpdating(true);
    setUpdateMessage('Funkcija je pokrenuta i radi u pozadini. Možete slobodno nastaviti sa radom dok se ne završi.');
    
    try {
      console.log('Pozivanje Cloud Function...');
      
      const response = await fetch('https://updatesheetsfromfirestore-syg4jytmeq-uc.a.run.app', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.text();
        console.log('Uspješno pozvano:', result);
        
        if (result === 'Update completed') {
          // Potvrđivanje svih rasporeda
          for (const schedule of weekSchedules) {
            if (!schedule.confirmed && schedule.id) {
              try {
                const scheduleRef = doc(db, 'weekSchedule', schedule.id);
                await updateDoc(scheduleRef, {
                  confirmed: true,
                });
                
                // Ažuriramo lokalno stanje
                updateWeekSchedule({
                  ...schedule,
                  confirmed: true,
                  generated: schedule.generated || false,
                });
              } catch (error) {
                console.error('Greška pri potvrđivanju rasporeda:', error);
              }
            }
          }
          
          setUpdateMessage('Funkcija uspješno završena i svi rasporedi potvrđeni!');
        } else {
          setUpdateMessage('Funkcija pozvana, ali ažuriranje nije završeno uspješno.');
        }
      } else {
        console.error('Server greška:', response.status, response.statusText);
        setUpdateMessage(`Greška servera: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error triggering function:', error);
      setUpdateMessage('Greška pri pozivanju funkcije. Provjerite konzolu za detalje.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="raspored-container">
      {currentUser ? (
        <div>
          {/* Filtri */}
          <div className="filter-form">
            <label>Nacionalnost:</label>
            <select
              value={selectedNationality}
              onChange={(e) => setSelectedNationality(e.target.value)}
            >
              <option value="Sve">Sve</option>
              <option value="Hrvat">Hrvat</option>
              <option value="Stranac">Stranac</option>
            </select>

            <label>Firma:</label>
            <select
              value={selectedFirm}
              onChange={(e) => setSelectedFirm(e.target.value)}
            >
              <option value="">Sve</option>
              {firms
                .filter((firm) => firm.id !== 'default')
                .map((firm) => (
                  <option key={firm.id} value={firm.id}>
                    {firm.name}
                  </option>
                ))}
            </select>

            <label>Godina:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: new Date().getFullYear() + 1 - 2022 + 1 }, (_, i) => 2022 + i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <label>Tjedan:</label>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            >
              {Array.from({ length: 52 }, (_, i) => i + 1).map((week) => (
                <option key={week} value={week}>
                  {week}
                </option>
              ))}
            </select>

            <button 
              className="send-button" 
              onClick={triggerUpdate}
              disabled={isUpdating}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginLeft: '10px'
              }}
            >
              {isUpdating ? 'U tijeku...' : 'Pošalji'}
            </button>
          </div>

          {updateMessage && (
            <p style={{ color: 'green', marginTop: '10px' }}>{updateMessage}</p>
          )}

          {/* Navigacija kroz tjedne */}
          <div className="week-navigation">
            <button className="nav-button" onClick={handlePreviousWeek}>
              Prethodni tjedan
            </button>
            <span className="current-week">Tjedan {selectedWeek}</span>
            <button className="nav-button" onClick={handleNextWeek}>
              Sljedeći tjedan
            </button>
          </div>

          {/* Prikaz tablice */}

            <div className="raspored-table-wrapper">
              <RasporedTable
                people={people}
                weekSchedules={weekSchedules}
                updateWeekSchedule={updateWeekSchedule}
                selectedNationality={selectedNationality}
                selectedFirm={selectedFirm}
                selectedYear={selectedYear}
                selectedWeek={selectedWeek}
                firms={firms}
              />

          </div>
        </div>
      ) : (
        <Login />
      )}
    </div>
  );
}

export default Raspored;